<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { Check, Close, Edit, FullScreen } from '@element-plus/icons-vue'
import type { CheckboxValueType } from 'element-plus'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import CompletedIcon from '~/components/icons/CompletedIcon.vue'
import InProgressIcon from '~/components/icons/InProgressIcon.vue'
import RouteMap from '~/components/RouteMap.vue'
import { useVisitsStore } from '~/stores/visits'
import { calculateDuration } from '~/utils/dateTimeUtils'
import ClickToCopy from '~/components/ClickToCopy.vue'
import ContainerMovementsTable from '~/components/ContainerMovementsTable.vue'

// Props and emits
const props = defineProps<{
  modelValue: 'streets' | 'satellite'
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: 'streets' | 'satellite'): void
}>()

// State
const isFullScreen = ref(false)
const regularMapLoading = ref(false) // Separate loading for regular map
const fullScreenMapLoading = ref(false) // Separate loading for fullscreen map

const mapStyle = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const visitsStore = useVisitsStore()
const { selectedVisit, visitDetails, status } = storeToRefs(visitsStore)

// Transaction switching state
const transactionDialogVisible = ref(false)
const otherTransactions = ref<(typeof selectedVisit.value)[]>([])
const loadingOtherTransactions = ref(false)
const selectingTransaction = ref(false)

// Extract appointments and gate transactions from container moves
const extractedAppointments = computed(() => {
  if (!selectedVisit.value?.transaction) return []

  const appointments = []

  // Extract from inbound container moves
  if (selectedVisit.value.transaction.inbound_container_moves) {
    for (const move of selectedVisit.value.transaction
      .inbound_container_moves) {
      if (move.appointment) {
        appointments.push(move.appointment)
      }
    }
  }

  // Extract from outbound container moves
  if (selectedVisit.value.transaction.outbound_container_moves) {
    for (const move of selectedVisit.value.transaction
      .outbound_container_moves) {
      if (move.appointment) {
        appointments.push(move.appointment)
      }
    }
  }

  return appointments
})

const extractedGateTransactions = computed(() => {
  if (!selectedVisit.value?.transaction) return []

  const gateTransactions = []

  // Extract from inbound container moves
  if (selectedVisit.value.transaction.inbound_container_moves) {
    for (const move of selectedVisit.value.transaction
      .inbound_container_moves) {
      if (move.gate_transaction) {
        gateTransactions.push(move.gate_transaction)
      }
    }
  }

  // Extract from outbound container moves
  if (selectedVisit.value.transaction.outbound_container_moves) {
    for (const move of selectedVisit.value.transaction
      .outbound_container_moves) {
      if (move.gate_transaction) {
        gateTransactions.push(move.gate_transaction)
      }
    }
  }

  return gateTransactions
})

// Combined containers data organized by loading status
const combinedContainers = computed(() => {
  if (!selectedVisit.value?.transaction)
    return { empty: [] as any[], loaded: [] as any[] }

  const containerMap = new Map()

  // Process all appointments
  for (const appointment of extractedAppointments.value) {
    const key = `${appointment.container_number}-${appointment.loaded}`
    if (!containerMap.has(key)) {
      containerMap.set(key, {
        container_number: appointment.container_number,
        loaded: appointment.loaded,
        appointment: null,
        gate_transaction: null,
      })
    }
    containerMap.get(key).appointment = appointment
  }

  // Process all gate transactions
  for (const transaction of extractedGateTransactions.value) {
    const key = `${transaction.container_number}-${transaction.loaded}`
    if (!containerMap.has(key)) {
      containerMap.set(key, {
        container_number: transaction.container_number,
        loaded: transaction.loaded,
        appointment: null,
        gate_transaction: null,
      })
    }
    containerMap.get(key).gate_transaction = transaction
  }

  // Group by loading status
  const result = { empty: [] as any[], loaded: [] as any[] }
  for (const container of containerMap.values()) {
    if (container.loaded) {
      result.loaded.push(container)
    } else {
      result.empty.push(container)
    }
  }

  return result
})

// Helper function to convert transaction data to container movements format
const getTransactionContainers = (transaction: any) => {
  if (!transaction) return { empty: [], loaded: [] }

  const containerMap = new Map()

  // Process inbound appointments
  if (transaction.inbound_container_moves) {
    for (const move of transaction.inbound_container_moves) {
      if (move.appointment) {
        const key = `${move.appointment.container_number}-${move.appointment.loaded}`
        if (!containerMap.has(key)) {
          containerMap.set(key, {
            container_number: move.appointment.container_number,
            loaded: move.appointment.loaded,
            appointment: null,
            gate_transaction: null,
          })
        }
        containerMap.get(key).appointment = move.appointment
      }
      if (move.gate_transaction) {
        const key = `${move.gate_transaction.container_number}-${move.gate_transaction.loaded}`
        if (!containerMap.has(key)) {
          containerMap.set(key, {
            container_number: move.gate_transaction.container_number,
            loaded: move.gate_transaction.loaded,
            appointment: null,
            gate_transaction: null,
          })
        }
        containerMap.get(key).gate_transaction = move.gate_transaction
      }
    }
  }

  // Process outbound appointments
  if (transaction.outbound_container_moves) {
    for (const move of transaction.outbound_container_moves) {
      if (move.appointment) {
        const key = `${move.appointment.container_number}-${move.appointment.loaded}`
        if (!containerMap.has(key)) {
          containerMap.set(key, {
            container_number: move.appointment.container_number,
            loaded: move.appointment.loaded,
            appointment: null,
            gate_transaction: null,
          })
        }
        containerMap.get(key).appointment = move.appointment
      }
      if (move.gate_transaction) {
        const key = `${move.gate_transaction.container_number}-${move.gate_transaction.loaded}`
        if (!containerMap.has(key)) {
          containerMap.set(key, {
            container_number: move.gate_transaction.container_number,
            loaded: move.gate_transaction.loaded,
            appointment: null,
            gate_transaction: null,
          })
        }
        containerMap.get(key).gate_transaction = move.gate_transaction
      }
    }
  }

  // Group by loading status
  const result = { empty: [] as any[], loaded: [] as any[] }
  for (const container of containerMap.values()) {
    if (container.loaded) {
      result.loaded.push(container)
    } else {
      result.empty.push(container)
    }
  }

  return result
}

// Function declarations
const handleNotBillableChange = (value: CheckboxValueType) => {
  visitsStore.setNotBillable(value === true)
}

const toggleFullScreen = () => {
  isFullScreen.value = !isFullScreen.value
}

// Transaction switching functions
const openTransactionDialog = async () => {
  if (!selectedVisit.value) return

  transactionDialogVisible.value = true
  loadingOtherTransactions.value = true

  try {
    const transactions = await visitsStore.getPossibleTransactions(
      selectedVisit.value.id
    )
    otherTransactions.value = transactions
  } catch (error) {
    console.error('Error loading other transactions:', error)
    ElMessage.error('Failed to load other transactions')
  } finally {
    loadingOtherTransactions.value = false
  }
}

const selectOtherTransaction = async (
  transaction: typeof selectedVisit.value
) => {
  if (!selectedVisit.value || !transaction?.transaction?.id) return

  selectingTransaction.value = true

  try {
    await visitsStore.selectTransaction(
      selectedVisit.value.id,
      transaction.transaction.id
    )
    transactionDialogVisible.value = false
    ElMessage.success('Transaction updated successfully')
  } catch (error) {
    console.error('Error selecting transaction:', error)
    ElMessage.error('Failed to update transaction')
  } finally {
    selectingTransaction.value = false
  }
}

const closeTransactionDialog = () => {
  transactionDialogVisible.value = false
  otherTransactions.value = []
}

// Determine if device ID is Samsara (all digits, more than 5 characters) or Geotab
const isSamsaraDevice = (deviceId: string): boolean => {
  return /^\d+$/.test(deviceId) && deviceId.length > 5
}

// Generate Samsara URL for device tracking
const generateSamsaraUrl = (
  deviceId: string,
  arrivalTimestamp: string,
  departureTimestamp?: string
) => {
  if (!arrivalTimestamp) {
    return `https://cloud.samsara.com/devices/${deviceId}/vehicle`
  }

  const arrivalMs = new Date(arrivalTimestamp).getTime()

  // Validate arrival timestamp
  if (isNaN(arrivalMs)) {
    return `https://cloud.samsara.com/devices/${deviceId}/vehicle`
  }

  let endMs: number

  if (!departureTimestamp) {
    // For in-progress visits, use current time
    endMs = Date.now()
  } else {
    endMs = new Date(departureTimestamp).getTime()
    // Validate departure timestamp, fallback to current time if invalid
    if (isNaN(endMs)) {
      endMs = Date.now()
    }
  }

  // Ensure duration is not negative if endMs is before arrivalMs
  const durationMs = Math.max(0, endMs - arrivalMs)
  const durationSeconds = Math.floor(durationMs / 1000)

  return `https://cloud.samsara.com/devices/${deviceId}/vehicle?end_ms=${endMs}&duration=${durationSeconds}`
}

// Generate Geotab URL for device tracking
const generateGeotabUrl = (
  deviceId: string,
  arrivalTimestamp: string,
  departureTimestamp?: string
) => {
  if (!arrivalTimestamp) {
    return `https://my.geotab.com/henean_trucking/#tripsHistory,devices:!(${deviceId}),view:assets`
  }

  const arrivalDate = new Date(arrivalTimestamp)
  let departureDate: Date

  if (!departureTimestamp) {
    // For in-progress visits, use current time
    departureDate = new Date()
  } else {
    departureDate = new Date(departureTimestamp)
    // Validate departure timestamp, fallback to current time if invalid
    if (isNaN(departureDate.getTime())) {
      departureDate = new Date()
    }
  }

  // Validate arrival timestamp
  if (isNaN(arrivalDate.getTime())) {
    return `https://my.geotab.com/henean_trucking/#tripsHistory,devices:!(${deviceId}),view:assets`
  }

  // Format dates to ISO string for Geotab
  const startTime = arrivalDate.toISOString()
  const endTime = departureDate.toISOString()

  // Construct the Geotab URL with the required format
  return `https://my.geotab.com/henean_trucking/#tripsHistory,dateRange:(endDate:'${endTime}',startDate:'${startTime}'),devices:!(${deviceId}),isFiltersOpen:!f,routes:(${deviceId}:!((start:'${startTime}',stop:'${endTime}'))),view:assets`
}

// Generate appropriate device tracking URL based on device type
const generateDeviceTrackingUrl = (
  deviceId: string,
  arrivalTimestamp: string,
  departureTimestamp?: string
) => {
  if (isSamsaraDevice(deviceId)) {
    return generateSamsaraUrl(deviceId, arrivalTimestamp, departureTimestamp)
  } else {
    return generateGeotabUrl(deviceId, arrivalTimestamp, departureTimestamp)
  }
}

// Helper function to format strategy names for display
const formatStrategy = (strategy: string) => {
  const strategyMap: Record<string, string> = {
    // Visit linking strategies
    exact_truck_and_overlap: 'Exact Truck Match',
    exact_truck_proximity: 'Exact Truck with Time Proximity Match',
    time_overlap_only: 'Time-Based Match',

    // Transaction linking strategies
    truck_visit_appointment_id_matching: 'Truck Visit Appointment ID Match',
    linked_appointments_matching: 'Linked Appointments',
    driver_license_number_matching: 'Driver License Match',
    truck_license_plate_number_matching: 'Truck License Plate Match',
    truck_rfid_number_matching: 'RFID Tag Match',
    conservative_time_clustering: 'Time Clustering',
    unmatched_moves_matching: 'Unmatched Moves',
  }

  return (
    strategyMap[strategy] ||
    strategy
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  )
}

// Helper function to get strategy descriptions
const getStrategyDescription = (
  strategy: string,
  type: 'visit' | 'transaction'
) => {
  const descriptions: Record<string, string> = {
    // Visit linking strategies
    exact_truck_and_overlap:
      'Visit matched using exact vehicle identification with time overlap',
    exact_truck_proximity:
      'Visit matched using exact vehicle identification with time proximity',
    time_overlap_only: 'Visit matched based on time overlap analysis only',

    // Transaction linking strategies
    truck_visit_appointment_id_matching:
      'Container Moves linked using Truck Visit Appointment ID from truck visit data',
    linked_appointments_matching:
      'Container Moves linked through linked appointments',
    driver_license_number_matching:
      'Container Moves linked using Driver License Number from truck visit data',
    truck_license_plate_number_matching:
      'Container Moves linked using Truck License Plate Number from truck visit data',
    truck_rfid_number_matching:
      'Container Moves linked using RFID Tag from truck visit data',
    conservative_time_clustering:
      'Container Moves linked using conservative time-based clustering analysis',
    unmatched_moves_matching:
      'Container Moves linked through unmatched move reconciliation',
  }

  return (
    descriptions[strategy] ||
    `${type} processed using ${formatStrategy(strategy).toLowerCase()} method`
  )
}

// Watch for fullscreen changes to handle body scroll
watch(
  () => isFullScreen.value,
  (newValue) => {
    // Prevent body scroll when in fullscreen
    document.body.style.overflow = newValue ? 'hidden' : ''
  }
)
</script>

<template>
  <!-- Full Screen Map Overlay -->
  <Teleport to="body">
    <div v-if="isFullScreen" class="fixed inset-0 z-50 bg-white">
      <div class="h-full flex flex-col">
        <div class="flex items-center justify-between p-4 border-b">
          <div class="flex items-center space-x-4">
            <h2 class="text-xl font-semibold text-gray-900">Route Map</h2>
            <el-radio-group v-model="mapStyle" size="small">
              <el-radio-button value="streets">Street</el-radio-button>
              <el-radio-button value="satellite">Satellite</el-radio-button>
            </el-radio-group>
          </div>
          <el-button type="default" @click="toggleFullScreen">
            <Close class="w-5 h-5" />
          </el-button>
        </div>
        <RouteMap
          v-if="isFullScreen"
          v-model="mapStyle"
          v-model:loading="fullScreenMapLoading"
          :is-full-screen="true"
          :selected-visit="selectedVisit"
          class="flex-1"
        />
      </div>
    </div>
  </Teleport>

  <div v-if="!selectedVisit" class="flex items-center justify-center h-full">
    <div class="text-center">
      <span class="text-4xl mb-4 block">👈</span>
      <p class="text-gray-500">Select a visit from the list to view details</p>
    </div>
  </div>

  <div v-else class="flex flex-col h-full">
    <div class="flex-1 overflow-auto">
      <!-- Conditionally render the regular map container -->
      <div
        v-if="!isFullScreen"
        class="bg-white rounded-lg shadow-sm border border-gray-100"
      >
        <!-- General Information Section -->
        <div class="px-4 py-3">
          <!-- Header with status badge -->
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-baseline space-x-2">
              <span class="text-xl font-bold">
                {{ visitDetails.totalTime }} at {{ visitDetails.locationName }}
              </span>
              <span class="font-bold">•</span>
              <span class="text-lg text-gray-500">
                {{
                  selectedVisit
                    ? DateTime.fromISO(selectedVisit.arrival).toFormat(
                        'EEE M/d'
                      )
                    : ''
                }}
              </span>
            </div>
            <div class="flex items-center">
              <span class="inline-flex items-center text-lg text-gray-900">
                Status:
                <span class="font-semibold ml-1">
                  {{ status === 'completed' ? 'Completed' : 'In Progress' }}
                </span>
                <component
                  :is="status === 'completed' ? CompletedIcon : InProgressIcon"
                  class="w-4 h-4 ml-2"
                  :class="
                    status === 'completed' ? 'text-green-600' : 'text-blue-600'
                  "
                />
              </span>
            </div>
          </div>

          <!-- Compact Info Row -->
          <div class="flex items-start justify-between">
            <div class="flex flex-col items-start text-md text-gray-500">
              <!-- Driver -->
              <div>
                <span>Driver: </span>
                <span
                  v-if="visitDetails?.driverName"
                  class="truncate"
                  :title="visitDetails.driverName"
                >
                  {{ visitDetails.driverName }}
                </span>
                <span v-else>No Driver</span>
              </div>
              <!-- Device Name -->
              <div>
                <span>Device Name: </span>
                <ClickToCopy
                  v-if="visitDetails?.deviceName"
                  :text-to-copy="visitDetails.deviceName"
                >
                  <span class="truncate" :title="visitDetails.deviceName">
                    {{ visitDetails.deviceName }}
                  </span>
                </ClickToCopy>
                <span v-else>No Name</span>
              </div>
              <!-- Device ID -->
              <div class="text-sm">
                <span>Device ID: </span>
                <a
                  v-if="
                    visitDetails?.externalDeviceId && selectedVisit?.arrival
                  "
                  :href="
                    generateDeviceTrackingUrl(
                      visitDetails?.externalDeviceId!,
                      selectedVisit?.arrival!,
                      selectedVisit?.departure
                    )
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center hover:underline"
                >
                  <ClickToCopy :text-to-copy="visitDetails.externalDeviceId">
                    {{ visitDetails.externalDeviceId }}
                  </ClickToCopy>
                  <span class="ml-1 text-xs text-gray-500">
                    ({{
                      isSamsaraDevice(visitDetails.externalDeviceId)
                        ? 'Samsara'
                        : 'Geotab'
                    }})
                  </span>
                  <i-mdi:external-link class="w-3 h-3 ml-1" />
                </a>
                <span v-else>No Device</span>
              </div>
              <!-- License -->
              <div class="text-sm">
                <span>License Plate: </span>
                <ClickToCopy
                  v-if="visitDetails?.licensePlate"
                  :text-to-copy="visitDetails.licensePlate"
                >
                  <span>
                    {{ visitDetails.licensePlate }}
                  </span>
                </ClickToCopy>
                <span v-else> - </span>
              </div>
            </div>

            <!-- Data Processing Summary (Compact) -->
            <div class="flex flex-col space-y-1 items-end">
              <div
                v-if="
                  selectedVisit?.linking_stats?.strategy ||
                  selectedVisit?.transaction?.strategy
                "
              >
                <el-popover
                  placement="bottom-end"
                  width="320"
                  trigger="hover"
                  :show-after="200"
                  :hide-after="100"
                >
                  <template #reference>
                    <el-button
                      type="info"
                      text
                      size="small"
                      class="!text-xs !px-2 !py-1 !h-6"
                    >
                      <i-mdi:database-cog class="w-3 h-3 mr-1" />
                      Processing Info
                    </el-button>
                  </template>
                  <div class="space-y-3">
                    <div
                      class="flex items-center space-x-1 pb-2 border-b border-gray-200"
                    >
                      <i-mdi:database-cog class="w-4 h-4 text-blue-600" />
                      <h4 class="text-sm font-semibold text-gray-900">
                        Data Processing Methods
                      </h4>
                    </div>
                    <div class="space-y-3">
                      <div v-if="selectedVisit?.linking_stats?.strategy">
                        <div class="flex items-center space-x-2 mb-1">
                          <i-mdi:map-marker-path
                            class="w-3 h-3 text-blue-500 flex-shrink-0"
                          />
                          <span
                            class="text-xs font-semibold text-gray-700 uppercase tracking-wide"
                          >
                            Visit Detection
                          </span>
                        </div>
                        <div class="pl-5">
                          <div class="text-xs font-medium text-gray-900 mb-1">
                            {{
                              formatStrategy(
                                selectedVisit.linking_stats.strategy
                              )
                            }}:
                            <span class="text-xs text-gray-500 leading-relaxed">
                              {{
                                getStrategyDescription(
                                  selectedVisit.linking_stats.strategy,
                                  'visit'
                                )
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div v-if="selectedVisit?.transaction?.strategy">
                        <div class="flex items-center space-x-2 mb-1">
                          <i-mdi:swap-horizontal
                            class="w-3 h-3 text-blue-500 flex-shrink-0"
                          />
                          <span
                            class="text-xs font-semibold text-gray-700 uppercase tracking-wide"
                          >
                            Transaction Linking
                          </span>
                        </div>
                        <div class="pl-5">
                          <div class="text-xs font-medium text-gray-900 mb-1">
                            {{
                              formatStrategy(
                                selectedVisit.transaction.strategy
                              )
                            }}:
                            <span class="text-xs text-gray-500 leading-relaxed">
                              {{
                                getStrategyDescription(
                                  selectedVisit.transaction.strategy,
                                  'transaction'
                                )
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </el-popover>
              </div>

              <!-- Switch Transaction Button -->
              <el-button
                v-if="selectedVisit?.transaction"
                type="primary"
                text
                size="small"
                class="!text-xs !px-2 !py-1 !h-6"
                @click="openTransactionDialog"
              >
                <i-mdi:swap-horizontal class="w-3 h-3 mr-1" />
                Switch Transaction
              </el-button>
            </div>
          </div>

          <!-- Appointment -->
          <div
            v-if="visitDetails?.appointment"
            class="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg"
          >
            <div class="flex items-center space-x-2">
              <i-mdi:calendar-clock
                class="w-3 h-3 text-amber-600 flex-shrink-0"
              />
              <span class="text-xs font-medium text-amber-800">{{
                visitDetails.appointment
              }}</span>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <!-- Combined Container Activities -->
        <ContainerMovementsTable :containers="combinedContainers" />

        <div class="h-px bg-gray-100" />

        <!-- Timeline and Financial Section -->
        <div class="px-4 py-3">
          <div class="grid grid-cols-2 gap-6">
            <!-- Timeline Section (Left Column) -->
            <div>
              <h3
                class="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3"
              >
                Time Breakdown
              </h3>

              <div class="space-y-3">
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span class="font-medium text-gray-900">Made line</span>
                  </div>
                  <div class="flex items-center space-x-2">
                    <el-button plain size="small" class="!px-2 !py-1" disabled>
                      <Edit class="w-3 h-3" />
                    </el-button>
                    <span class="text-gray-900 font-medium">{{
                      visitDetails?.lineTime
                    }}</span>
                  </div>
                </div>

                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span class="font-medium text-gray-900"
                      >Entered geo-fence</span
                    >
                  </div>
                  <span class="text-gray-900 font-medium">{{
                    visitDetails?.entryTime
                  }}</span>
                </div>

                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span class="font-medium text-gray-900"
                      >Exit geo-fence</span
                    >
                  </div>
                  <div class="text-gray-900 font-medium">
                    {{ visitDetails?.exitTime }}
                  </div>
                </div>
              </div>

              <!-- Total Duration Summary -->
              <div class="mt-3 pt-3 border-t border-gray-100">
                <div class="flex items-center justify-between text-sm">
                  <div class="text-xs text-gray-500 uppercase tracking-wide">
                    Total Duration
                  </div>
                  <div class="text-sm font-semibold text-gray-900">
                    {{
                      selectedVisit
                        ? calculateDuration(
                            selectedVisit.arrival,
                            selectedVisit.departure
                          )
                        : '---'
                    }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Section (Right Column) -->
            <div class="border-l border-gray-100 pl-6">
              <div class="flex items-start justify-between mb-3">
                <div>
                  <h3
                    class="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1"
                  >
                    Billing Summary
                  </h3>
                  <div class="text-xs text-gray-600">
                    {{ visitDetails?.totalTime }} at ${{
                      visitDetails?.hourlyRate
                    }}/hour
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-2xl font-bold text-gray-900">
                    ${{ visitDetails?.totalCharge }}
                  </div>
                  <div class="text-xs text-gray-500 uppercase tracking-wide">
                    Total
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <el-checkbox
                  class="!m-0"
                  :model-value="visitDetails?.isNotBillable"
                  size="small"
                  @update:model-value="handleNotBillableChange"
                >
                  <span class="text-xs text-gray-600">
                    Mark as non-billable
                  </span>
                </el-checkbox>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <el-button type="primary" size="small" class="!h-8">
                  <template #icon>
                    <Check class="w-3 h-3" />
                  </template>
                  Approve
                </el-button>
                <el-button type="danger" plain size="small" class="!h-8">
                  <template #icon>
                    <Close class="w-3 h-3" />
                  </template>
                  Reject
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <!-- Map Section -->
        <div class="p-4 border-t border-gray-100">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-lg font-semibold text-gray-900">Route Map</h3>
            <div class="flex items-center space-x-2">
              <el-radio-group v-model="mapStyle" size="small">
                <el-radio-button value="streets">Street</el-radio-button>
                <el-radio-button value="satellite">Satellite</el-radio-button>
              </el-radio-group>
              <el-button type="default" size="small" @click="toggleFullScreen">
                <FullScreen class="w-4 h-4" />
              </el-button>
            </div>
          </div>
          <RouteMap
            v-model="mapStyle"
            v-model:loading="regularMapLoading"
            :selected-visit="selectedVisit"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Transaction Selection Dialog -->
  <el-dialog
    v-model="transactionDialogVisible"
    :title="`Switch Transaction - Visit #${selectedVisit?.id}`"
    width="800px"
    :before-close="closeTransactionDialog"
  >
    <div v-if="loadingOtherTransactions" class="text-center py-8">
      <el-icon class="is-loading w-8 h-8 text-gray-400 mb-2">
        <i-mdi:loading />
      </el-icon>
      <p class="text-gray-500">Loading other transactions...</p>
    </div>

    <div v-else-if="otherTransactions.length === 0" class="text-center py-8">
      <p class="text-gray-500">
        No other transactions available for this visit.
      </p>
    </div>

    <div v-else class="space-y-3">
      <p class="text-sm text-gray-600 mb-3">
        Select a different transaction to link with this visit:
      </p>

      <div class="max-h-96 overflow-y-auto">
        <div
          v-for="(transactionOption, index) in otherTransactions"
          :key="transactionOption?.transaction?.id || index"
          class="p-3 cursor-pointer transition-colors duration-200 hover:bg-gray-50 border border-gray-200"
          :class="{
            'opacity-50 pointer-events-none': selectingTransaction,
            'border-blue-300 bg-blue-50':
              transactionOption?.transaction?.id ===
              selectedVisit?.transaction?.id,
          }"
          @click="selectOtherTransaction(transactionOption)"
        >
          <!-- Transaction Header -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center space-x-2">
              <h4 class="font-semibold text-gray-900 text-sm">
                Transaction #{{ transactionOption?.transaction?.id || 'N/A' }}
              </h4>
              <el-tag
                v-if="
                  transactionOption?.transaction?.id ===
                  selectedVisit?.transaction?.id
                "
                type="success"
                size="small"
                effect="light"
                class="!text-xs !font-medium"
              >
                <i-mdi:check-circle class="w-3 h-3 mr-1" />
                Selected
              </el-tag>
            </div>

            <!-- Strategy Popover -->
            <el-popover
              v-if="
                selectedVisit?.linking_stats?.strategy ||
                transactionOption?.transaction?.strategy
              "
              placement="left"
              width="320"
              trigger="hover"
              :show-after="200"
              :hide-after="100"
            >
              <template #reference>
                <el-button
                  type="info"
                  text
                  size="small"
                  class="!text-xs !px-2 !py-1 !h-6"
                >
                  <i-mdi:information-outline class="w-3 h-3" />
                </el-button>
              </template>
              <div class="space-y-3">
                <div
                  class="flex items-center space-x-1 pb-2 border-b border-gray-200"
                >
                  <i-mdi:cog class="w-4 h-4 text-blue-600" />
                  <h4 class="text-sm font-semibold text-gray-900">
                    Processing Strategies
                  </h4>
                </div>
                <div class="space-y-2">
                  <!-- Visit Linking Strategy -->
                  <div v-if="selectedVisit?.linking_stats?.strategy">
                    <div class="flex items-center space-x-2 mb-1">
                      <i-mdi:map-marker-path
                        class="w-3 h-3 text-blue-500 flex-shrink-0"
                      />
                      <span
                        class="text-xs font-semibold text-gray-700 uppercase tracking-wide"
                      >
                        Visit Detection
                      </span>
                    </div>
                    <div class="pl-5">
                      <div class="text-xs font-medium text-gray-900 mb-1">
                        {{
                          formatStrategy(selectedVisit.linking_stats.strategy)
                        }}
                      </div>
                      <div class="text-xs text-gray-500 leading-relaxed">
                        {{
                          getStrategyDescription(
                            selectedVisit.linking_stats.strategy,
                            'visit'
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <!-- Transaction Linking Strategy -->
                  <div v-if="transactionOption?.transaction?.strategy">
                    <div class="flex items-center space-x-2 mb-1">
                      <i-mdi:swap-horizontal
                        class="w-3 h-3 text-blue-500 flex-shrink-0"
                      />
                      <span
                        class="text-xs font-semibold text-gray-700 uppercase tracking-wide"
                      >
                        Transaction Linking
                      </span>
                    </div>
                    <div class="pl-5">
                      <div class="text-xs font-medium text-gray-900 mb-1">
                        {{
                          formatStrategy(transactionOption.transaction.strategy)
                        }}
                      </div>
                      <div class="text-xs text-gray-500 leading-relaxed">
                        {{
                          getStrategyDescription(
                            transactionOption.transaction.strategy,
                            'transaction'
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </el-popover>
          </div>

          <!-- Container Movements Table -->
          <ContainerMovementsTable
            v-if="transactionOption?.transaction"
            :containers="
              getTransactionContainers(transactionOption.transaction)
            "
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="closeTransactionDialog">Cancel</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-button--large {
  height: 44px;
  padding: 0 24px;
}

// Custom compact button styles
.el-button--small {
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
}

// Ensure checkbox is properly sized
.el-checkbox--small .el-checkbox__label {
  font-size: 12px;
}
</style>
